import React from "react";
import { useLocation, Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import {
  Home,
  Bot,
  Play,
  Settings,
  Brain,
  Database,
  PanelsTopLeft,
  MessageSquare,
  Inbox,
  Blocks,
  CreditCard,
} from "lucide-react";
import { useBots } from "@/hooks/useApi";
import AvvisoLimitePiano from "@/components/billing/AvvisoLimitePiano";
import AvvisoRisposte from "@/components/billing/AvvisoRisposte";
import { appById } from "@/components/integrations/registry";

const MainLayout = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { id: botId } = useParams();
  const { data: bots } = useBots();

  // Find current bot if we're in a bot-specific route
  let currentBot =
    botId && Array.isArray(bots) ? bots.find((bot) => bot._id === botId) : null;
  // Regex per ObjectId di MongoDB (24 caratteri esadecimali)
  const mongoObjectIdRegex = /^[0-9a-fA-F]{24}$/;

  // Utilizzo
  const isValidObjectId = (str) => mongoObjectIdRegex.test(str);

  // Generate enhanced breadcrumbs with icons and context
  const generateBreadcrumbs = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const breadcrumbs = [];

    // Always start with Home
    breadcrumbs.push({
      label: "",
      path: "/home",
      icon: Home,
    });

    // Add segments based on path
    let currentPath = "";
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      let label = segment.charAt(0).toUpperCase() + segment.slice(1);
      let icon = null;
      let isClickable = true;

      // Handle specific routes with context
      if (segment === "bots") {
        label = t("breadcrumb.bots", "Assistenti");
        icon = null;
      } else if (segment === "admin") {
        label = t("nav.clients", "Clienti");
      } else if (segment === "home") {
        label = t("common.dashboard", "Dashboard");
      } else if (segment === "organization") {
        label = t("common.organization", "Organizzazione");
      } else if (isValidObjectId(segment)) {
        currentBot = Array.isArray(bots)
          ? bots.find((bot) => bot._id === segment)
          : null;
        label = currentBot?.name || "Bot";
        icon = Bot;
      } else if (segment === "sources") {
        label = t("nav.sources", "Fonti");
        icon = Database;
        // Make clickable only if we have bot context
        isClickable = !!currentBot;
      } else if (segment === "playground") {
        label = "Playground";
        icon = Play;
        isClickable = !!currentBot;
      } else if (segment === "widget") {
        label = "Widget";
        icon = PanelsTopLeft;
        isClickable = !!currentBot;
      } else if (pathSegments[index - 1] === "integrazioni" && appById(segment)) {
        // Pagina di un'app di Apps & Integrazioni: il suo nome dal registro.
        label = appById(segment).title;
        icon = null;
        isClickable = false;
      } else if (segment === "settings") {
        label = t("common.settings", "Impostazioni");
        icon = Settings;
        isClickable = !!currentBot;
      } else if (segment === "analytics") {
        label = t("nav.conversations", "Conversazioni");
        icon = MessageSquare;
        isClickable = !!currentBot;
      } else if (segment === "conversations") {
        label = t("nav.conversations", "Conversazioni");
        icon = MessageSquare;
        isClickable = !!currentBot;
      } else if (segment === "intelligence") {
        label = t("nav.analytics", "Analisi");
        icon = Brain;
        isClickable = !!currentBot;
      } else if (segment === "contatti") {
        label = t("nav.contactForm", "Contatti");
        icon = Inbox;
        isClickable = !!currentBot;
      } else if (segment === "integrazioni") {
        label = t("nav.integrations", "Apps & Integrazioni");
        icon = Blocks;
        isClickable = !!currentBot;
      } else if (segment === "billing") {
        label = t("breadcrumb.billing", "Abbonamento");
        icon = CreditCard;
      } else if (segment === "new") {
        label = t("breadcrumb.newBot", "Nuovo assistente");
        isClickable = false; // Don't make "new" clickable
      }

      // Skip adding UUIDs to breadcrumbs unless they're the bot ID
      const isUUID =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
          segment
        );
      if (isUUID && segment !== botId) {
        return; // Skip UUID segments that aren't bot IDs
      }

      breadcrumbs.push({
        label,
        path: isClickable ? currentPath : null,
        icon,
        isLast: index === pathSegments.length - 1,
        isClickable,
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  return (
    <SidebarProvider>
      <AvvisoLimitePiano />
      <AppSidebar />
      {/* clip e non hidden: taglia lo stesso cio' che esce ai lati (gli
          sfondi delle sezioni), ma non fa di main un contenitore che scorre,
          che terrebbe fermi gli elementi sticky (anteprime, schede). */}
      <main className="flex flex-1 flex-col min-w-0 overflow-x-clip">
        {/* Header with breadcrumbs */}
        {/* relative z-10: resta sopra agli sfondi delle pagine che salgono fin
            qui dietro (Apps & Integrazioni). */}
        <header className="relative z-10 flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={`${index}-${crumb.path || crumb.label}`}>
                    <BreadcrumbItem>
                      {crumb.isLast ? (
                        <BreadcrumbPage className="flex items-center font-medium">
                          {crumb.icon && (
                            <crumb.icon className="h-4 w-4 mr-2" />
                          )}
                          {crumb.label}
                        </BreadcrumbPage>
                      ) : crumb.isClickable && crumb.path ? (
                        <BreadcrumbLink asChild>
                          <Link
                            to={crumb.path}
                            className="flex items-center hover:text-foreground transition-colors"
                          >
                            {crumb.icon && (
                              <crumb.icon className="h-4 w-4 mr-2" />
                            )}
                            {crumb.label}
                          </Link>
                        </BreadcrumbLink>
                      ) : (
                        <span className="flex items-center text-muted-foreground">
                          {crumb.icon && (
                            <crumb.icon className="h-4 w-4 mr-2" />
                          )}
                          {crumb.label}
                        </span>
                      )}
                    </BreadcrumbItem>
                    {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>

        {/* Main content area */}
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0 min-w-0">
          <AvvisoRisposte />
          {children}
        </div>
      </main>
    </SidebarProvider>
  );
};

export default MainLayout;
